import {
  createContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from "react";
import { UserRoles } from "../enums/userRoles.enum";
import { dashLogin } from "../Api/authentication";
import {
  setAuthtenticated,
  setToken,
  setUser as setReduxUser,
} from "../features/Dashboard/Slices/userSlice";
import { decodeJWT } from "../utils";
import { validateToken } from "../features/Dashboard/AsyncThunks/estadoGeneral/validateToken";
import { isRejected } from "@reduxjs/toolkit";
import { useAppDispatch } from "../app/store";

export type PermissionsObject = {
  [module: string]: string[];
};

export type User = {
  token?: string;
  permisos?: PermissionsObject;
  info?: any;
} | null;

export type AuthContextType = {
  user: User;
  setUser: React.Dispatch<React.SetStateAction<User>>;
  permissions: PermissionsObject;
  setPermissions: React.Dispatch<React.SetStateAction<PermissionsObject>>;
  hasPermission: (module: string, permission: string) => boolean;
  isLoading: boolean;
};

export const AuthContext = createContext<AuthContextType>({
  user: null,
  setUser: () => {},
  permissions: {},
  setPermissions: () => {},
  hasPermission: () => false,
  isLoading: false,
});

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User>(null);
  const [permissions, setPermissions] = useState<PermissionsObject>({});
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useAppDispatch();

  const hasPermission = useCallback(
    (module: string, permission: string): boolean => {
      if (!permissions || !permissions[module]) return false;
      if (user?.info.rol === UserRoles.SUPER_ADMIN) return true;
      return permissions[module].includes(permission);
    },
    [permissions]
  );

  const restoreSession = async () => {
    setIsLoading(true);
    const token = localStorage.getItem("token");

    if (token) {
      const email = decodeJWT(token)?.email;
      if (email) {
        try {
          const authenticatedResult = await dispatch(
            validateToken(token)
          );
          if (isRejected(authenticatedResult)) {
            throw new Error("Token is invalid on restore session");
          }
          const response = await dashLogin(email);
          if (response.token) {
            dispatch(setToken(response.token));
            dispatch(setAuthtenticated(true));
            if (response.info) {
              dispatch(setReduxUser(response.info));
            }
            setUser(response);
            setPermissions(response.info?.permisos || {});
          } else {
            localStorage.removeItem("token");
            setUser(null);
            setPermissions({});
            dispatch(setAuthtenticated(false));
            dispatch(setToken(null));
          }
        } catch (error) {
          console.error("Error al restaurar sesión:", error);
          localStorage.removeItem("token");
          localStorage.removeItem("email");
          setUser(null);
          setPermissions({});
          dispatch(setAuthtenticated(false));
          dispatch(setToken(null));
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    restoreSession();
  }, []);

  const value = {
    user,
    setUser,
    permissions,
    setPermissions,
    hasPermission,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
